import React, { useEffect, useMemo, useState } from 'react';
import { adminGetCompany } from '../../../api/packagesApi';
import {
  getCompanySubscription,
  getGracePeriodDays,
  resolveSubscriptionStatus,
  toSubscriptionDate,
} from '../../../../packages/utils/subscriptionStatus';
import { computeAnnualPrice } from '../../../../packages/utils/packagePricing';
import { adjustCompanySubscription } from '../companyPackageApi';
import '../package-dialogs.css';

function useEscapeToClose(open, onClose, disabled) {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !disabled) onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose, disabled]);
}

const AdjustSubscriptionDialog = ({ open, onClose, companyId, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [company, setCompany] = useState(null);
  const [subscription, setSubscription] = useState(null);

  const [daysToAdd, setDaysToAdd] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [autoRenew, setAutoRenew] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [priceOverride, setPriceOverride] = useState('');

  const isPermanent = !!subscription?.isPermanent;

  useEscapeToClose(open, onClose, loading);

  const monthlyPrice = useMemo(() => {
    if (!company?.package?.price?.monthly) return 0;
    const parsed = parseFloat(company.package.price.monthly);
    return isNaN(parsed) ? 0 : parsed;
  }, [company]);

  const annualDiscount = useMemo(() => {
    const configured = company?.package?.subscription?.annualDiscount;
    if (typeof configured === 'number') return configured;
    return 0;
  }, [company]);

  const computedPriceForCycle = useMemo(() => {
    if (billingCycle === 'annually') {
      return computeAnnualPrice(monthlyPrice, annualDiscount).toFixed(2);
    }
    return monthlyPrice.toFixed(2);
  }, [billingCycle, monthlyPrice, annualDiscount]);

  useEffect(() => {
    const load = async () => {
      if (!open || !companyId) return;
      setLoading(true);
      setError(null);
      try {
        const companyData = await adminGetCompany(companyId);
        if (!companyData) throw new Error('Company not found');
        setCompany(companyData);
        const rawSub = getCompanySubscription(companyData);
        const resolved = resolveSubscriptionStatus(rawSub, getGracePeriodDays(companyData));
        const sub = rawSub ? { ...rawSub, ...resolved } : null;
        setSubscription(sub);
        if (sub) {
          setAutoRenew(!!sub.autoRenew);
          setBillingCycle(sub.billingCycle || 'monthly');
          setPriceOverride(sub.currentPrice != null ? String(sub.currentPrice) : '');
          const end = toSubscriptionDate(sub.endDate);
          if (end) {
            const yyyy = end.getFullYear();
            const mm = String(end.getMonth() + 1).padStart(2, '0');
            const dd = String(end.getDate()).padStart(2, '0');
            setCustomEndDate(`${yyyy}-${mm}-${dd}`);
          }
        }
      } catch (e) {
        setError(e.message || 'Failed to load subscription');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open, companyId]);

  const handleExtendDays = async () => {
    if (!daysToAdd || parseInt(daysToAdd, 10) <= 0) {
      setError('Enter a valid number of days');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await adjustCompanySubscription({
        companyId,
        daysToAdd: parseInt(daysToAdd, 10),
        reason: 'Manual admin extension',
      });
      setDaysToAdd('');
      await onSuccess?.();
      onClose();
    } catch (e) {
      setError(e.message || 'Failed to extend subscription');
    } finally {
      setLoading(false);
    }
  };

  const handleSetEndDate = async () => {
    if (!customEndDate) {
      setError('Select a new end date');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await adjustCompanySubscription({
        companyId,
        endDate: customEndDate,
      });
      await onSuccess?.();
      onClose();
    } catch (e) {
      setError(e.message || 'Failed to update end date');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBillingAndAutoRenew = async () => {
    try {
      setLoading(true);
      setError(null);
      await adjustCompanySubscription({
        companyId,
        billingCycle,
        autoRenew,
        priceOverride: priceOverride !== '' ? priceOverride : undefined,
      });
      await onSuccess?.();
      onClose();
    } catch (e) {
      setError(e.message || 'Failed to save subscription settings');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="pkg-dialog-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="adjust-subscription-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !loading) onClose();
      }}
    >
      <div className="pkg-dialog">
        <header className="pkg-dialog-header">
          <h2 id="adjust-subscription-title" className="pkg-dialog-title">Adjust Subscription</h2>
        </header>

        <div className="pkg-dialog-body">
          {error && (
            <div className="pkg-dialog-alert pkg-dialog-alert--error" role="alert">
              {error}
            </div>
          )}

          {loading && !subscription && (
            <p className="pkg-dialog-muted">Loading subscription…</p>
          )}

          {!subscription && !loading && (
            <p className="pkg-dialog-muted">No subscription found for this company.</p>
          )}

          {subscription && (
            <>
              <section className="pkg-dialog-section">
                <span className="pkg-dialog-label">Current plan</span>
                <p className="pkg-dialog-text">
                  {company?.package?.name || 'Package'} •{' '}
                  {subscription.billingCycle === 'annually' ? 'Annual' : 'Monthly'} • $
                  {subscription.currentPrice?.toFixed
                    ? subscription.currentPrice.toFixed(2)
                    : subscription.currentPrice}
                </p>
              </section>

              <section className="pkg-dialog-section">
                <span className="pkg-dialog-label">Extend by days</span>
                <div className="pkg-dialog-row">
                  <input
                    type="number"
                    className="pkg-dialog-input"
                    placeholder="Days"
                    value={daysToAdd}
                    onChange={(e) => setDaysToAdd(e.target.value)}
                    disabled={loading || isPermanent}
                    min={1}
                  />
                  <button
                    type="button"
                    className="pkg-btn pkg-btn-secondary"
                    onClick={handleExtendDays}
                    disabled={loading || isPermanent}
                  >
                    Add days
                  </button>
                </div>
                {isPermanent && (
                  <p className="pkg-dialog-muted" style={{ marginTop: 8 }}>
                    Permanent subscriptions cannot be extended.
                  </p>
                )}
              </section>

              <section className="pkg-dialog-section">
                <span className="pkg-dialog-label">Set exact end date</span>
                <div className="pkg-dialog-row">
                  <input
                    type="date"
                    className="pkg-dialog-input"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    disabled={loading || isPermanent}
                  />
                  <button
                    type="button"
                    className="pkg-btn pkg-btn-secondary"
                    onClick={handleSetEndDate}
                    disabled={loading || isPermanent || !customEndDate}
                  >
                    Update end date
                  </button>
                </div>
              </section>

              <section className="pkg-dialog-section">
                <span className="pkg-dialog-label">Billing and renewal</span>
                <div className="pkg-dialog-field">
                  <label htmlFor="adjust-billing-cycle">Billing cycle</label>
                  <select
                    id="adjust-billing-cycle"
                    className="pkg-dialog-select"
                    value={billingCycle}
                    onChange={(e) => setBillingCycle(e.target.value)}
                    disabled={loading}
                  >
                    <option value="monthly">Monthly</option>
                    <option value="annually">Annual</option>
                  </select>
                </div>

                <label className="pkg-dialog-check">
                  <input
                    type="checkbox"
                    checked={autoRenew}
                    onChange={(e) => setAutoRenew(e.target.checked)}
                    disabled={loading}
                  />
                  Auto renew
                </label>

                <div className="pkg-dialog-row" style={{ marginTop: 12 }}>
                  <input
                    type="number"
                    className="pkg-dialog-input"
                    placeholder="Price override"
                    value={priceOverride}
                    onChange={(e) => setPriceOverride(e.target.value)}
                    disabled={loading}
                  />
                  <span className="pkg-dialog-muted">Default: ${computedPriceForCycle}</span>
                </div>
              </section>
            </>
          )}
        </div>

        <footer className="pkg-dialog-footer">
          <button type="button" className="pkg-btn pkg-btn-secondary" onClick={onClose} disabled={loading}>
            Close
          </button>
          <button
            type="button"
            className="pkg-btn pkg-btn-primary"
            onClick={handleSaveBillingAndAutoRenew}
            disabled={loading || !subscription}
          >
            Save
          </button>
        </footer>
      </div>
    </div>
  );
};

export default AdjustSubscriptionDialog;
